import { ConsultDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import { executeQuery } from 'app/api/client/graphqlClient'
import {
    consultDtosByOwnerAndStartDateQuery,
    consultCountByOwnerSinceQuery,
    getConsultDtoQuery,
} from 'app/graphql/customQueries'
import { buildConsultTagFilter } from 'app/api/consult/repository/consultTagFilter'

/** ConsultDto with the _deleted field from GraphQL responses */
type ConsultDtoWithDeleted = ConsultDto & { _deleted?: boolean | null }

export interface ConsultPage {
    items: ConsultDto[]
    nextToken: string | null
}

interface ListConsultDtosResult {
    items: ConsultDtoWithDeleted[]
    nextToken: string | null
}

export const consultQueryRepository = {
    /**
     * List consults for an owner, paginated.
     * Returns raw DTOs — no domain model mapping.
     */
    async listByOwner(
        owner: string,
        limit: number,
        nextToken?: string | null,
        tagFilters?: string[]
    ): Promise<ConsultPage> {
        try {
            // Query the byOwner GSI sorted by startDate DESC (newest first) so the
            // first page is the genuinely-newest consults. (Replaces listConsultDtos
            // scan+filter, which returned arbitrary table order and could drop the
            // newest consults from the first page — broke Consult History.)
            const variables: Record<string, unknown> = {
                owner,
                sortDirection: 'DESC',
                limit,
            }
            if (nextToken) {
                variables.nextToken = nextToken
            }

            // ACA-123: server-side tag search. When tags are supplied, DynamoDB
            // filters within the owner partition so the search covers ALL of the
            // clinician's consults, not just the pages the client has loaded.
            const tagFilter = buildConsultTagFilter(tagFilters ?? [])
            if (tagFilter) {
                variables.filter = tagFilter
            }

            const result = await executeQuery<ListConsultDtosResult>(
                consultDtosByOwnerAndStartDateQuery,
                variables,
                'consultDtosByOwnerAndStartDate'
            )

            const items = result.items.filter(
                (i) => !i._deleted
            ) as ConsultDto[]

            return {
                items,
                nextToken: result.nextToken,
            }
        } catch (error) {
            logger.error(
                'Failed to list consults by owner',
                error instanceof Error ? error : new Error(String(error)),
                {
                    action: 'list_consults_by_owner_error',
                    metadata: { owner },
                }
            )
            throw error
        }
    },

    /**
     * Get a single consult by ID.
     * Returns the raw DTO — no domain model mapping.
     */
    async getById(id: string): Promise<ConsultDto | null> {
        try {
            const result = await executeQuery<ConsultDtoWithDeleted | null>(
                getConsultDtoQuery,
                { id },
                'getConsultDto'
            )

            if (!result || result._deleted) {
                return null
            }

            return result as ConsultDto
        } catch (error) {
            logger.error(
                'Failed to get consult by id',
                error instanceof Error ? error : new Error(String(error)),
                {
                    action: 'get_consult_by_id_error',
                    metadata: { consultId: id },
                }
            )
            throw error
        }
    },

    /**
     * ACA-185: count the consults an owner CREATED with a startDate at or after
     * `sinceIso` (the admin "consults this month" figure).
     *
     * Counts every non-deleted consult the user started in the period, in any
     * state — a consult a clinician started this month should reflect in the
     * count even if it did not finish note generation (retry / formPending /
     * etc). Uses the lightweight id-only query (no PHI) and pages the byOwner
     * GSI, bounded to `sinceIso` by the startDate sort-key condition.
     */
    async countCreatedByOwnerSince(
        owner: string,
        sinceIso: string
    ): Promise<number> {
        let count = 0
        let nextToken: string | null = null
        do {
            const result: ListConsultDtosResult =
                await executeQuery<ListConsultDtosResult>(
                    consultCountByOwnerSinceQuery,
                    {
                        owner,
                        startDate: { ge: sinceIso },
                        limit: 1000,
                        ...(nextToken ? { nextToken } : {}),
                    },
                    'consultDtosByOwnerAndStartDate'
                )
            count += result.items.filter((i) => !i._deleted).length
            nextToken = result.nextToken
        } while (nextToken)
        return count
    },

    /**
     * ACA-196: ids of ALL the owner's live consults, fetched independently of
     * the paged consult cache. Selects only id/state/_deleted (reuses the count
     * query) so it stays cheap for users with many consults.
     */
    async listIdsByOwner(owner: string): Promise<string[]> {
        const ids: string[] = []
        let nextToken: string | null = null
        do {
            const result: ListConsultDtosResult =
                await executeQuery<ListConsultDtosResult>(
                    consultCountByOwnerSinceQuery,
                    {
                        owner,
                        limit: 1000,
                        ...(nextToken ? { nextToken } : {}),
                    },
                    'consultDtosByOwnerAndStartDate'
                )
            result.items.forEach((i) => {
                if (!i._deleted) ids.push(i.id)
            })
            nextToken = result.nextToken
        } while (nextToken)
        return ids
    },
}
